// pipe (site scope): the VISIBLE "This site ⇄ All sites" switch, relocated from the left
// sidebar to the workbench top bar (owner request 2026-08-02 — the panel keeps only "Sites";
// a data-view control belongs with the view chrome, beside the info-tips toggle). Same gates
// as before — super-admin only + a site-bound workspace — plus ERP routes only (the switch is
// meaningless on doc/canvas screens). The always-on workspace→scope sync lives separately in
// workspace-sync.tsx; this component is presentation + the flip, which remounts the ERP
// screens via ErpScopeBoundary exactly as before.
import { Button, Tooltip } from '@affine/component';
import { useErpProvisioning } from '@affine/core/modules/erp';
import { isErpPath } from '@affine/core/modules/erp/erp-routes';
import {
  erpSiteScope$,
  facilityForWorkspace,
  setAllSites,
} from '@affine/core/modules/erp/erp-site-scope';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { MultiSelectIcon, SingleSelectIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';

import { ViewService } from '../../modules/workbench/services/view';
import * as styles from './header-toggle.css';

export const SiteScopeHeaderToggle = () => {
  const t = useI18n();
  const provisioning = useErpProvisioning();
  const workspace = useService(WorkspaceService).workspace;
  const scope = useLiveData(erpSiteScope$);
  const view = useService(ViewService).view;
  const location = useLiveData(view.location$);

  const facility = facilityForWorkspace(provisioning, workspace.id);
  if (
    !isErpPath(location.pathname) ||
    !provisioning?.isSuperAdmin ||
    !facility
  ) {
    return null;
  }

  const label = scope.allSites
    ? t['com.affine.pipe.scope.all-sites']()
    : t['com.affine.pipe.scope.this-site']({ name: facility.name });

  // The visible, state-bearing label IS the accessible name (no aria-label override — the
  // hint would mask the state from assistive tech); aria-pressed mirrors the sibling
  // InfoTipsHeaderToggle pattern. The tooltip leads with the full (possibly truncated) label.
  return (
    <Tooltip
      content={`${label} — ${t['com.affine.pipe.scope.switch-hint']()}`}
    >
      <Button
        variant="plain"
        className={styles.scopeButton}
        prefix={scope.allSites ? <MultiSelectIcon /> : <SingleSelectIcon />}
        onClick={() => setAllSites(!scope.allSites)}
        aria-pressed={scope.allSites}
        data-testid="pipe-site-scope-toggle"
      >
        {label}
      </Button>
    </Tooltip>
  );
};
